import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { BlogPost, BlogComment, User, UserType } from '../types';
import './BlogPost.css';
import './Blog.css';

interface BlogPostViewProps {
    isAdmin?: boolean;
    skipNavbar?: boolean;
}

const BlogPostView: React.FC<BlogPostViewProps> = ({ isAdmin = false, skipNavbar = false }) => {
    const { postId } = useParams<{ postId: string }>();
    const navigate = useNavigate();
    const [post, setPost] = useState<BlogPost | null>(null);
    const [comments, setComments] = useState<BlogComment[]>([]);
    const [newComment, setNewComment] = useState<string>('');
    const [commentStatus, setCommentStatus] = useState<{ success: boolean; message: string } | null>(null);
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editContent, setEditContent] = useState<string>('');

    useEffect(() => {
        // Check if user is logged in
        const userJson = localStorage.getItem('user');
        if (userJson) {
            try {
                const userData = JSON.parse(userJson);
                setUser(userData);
            } catch (err) {
                console.error('Error parsing user data:', err);
            }
        }

        if (postId) {
            fetchPost(postId);
            fetchComments(postId);
        }
    }, [postId]);

    const fetchPost = async (id: string) => {
        try {
            const response = await fetch(`${API_URL}/blog/${id}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('sessionId')}`,
                },
            });

            if (response.ok) {
                const postData = await response.json();
                setPost(postData);
            } else if (response.status === 404) {
                setError('Blog post not found');
            } else {
                setError('Failed to load blog post');
            }
        } catch (err) {
            console.error('Error fetching post:', err);
            setError('An error occurred while loading the post');
        } finally {
            setLoading(false);
        }
    };

    const fetchComments = async (postId: string) => {
        try {
            const response = await fetch(`${API_URL}/blog/${postId}/comments`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('sessionId')}`,
                },
            });

            if (response.ok) {
                const commentsData = await response.json();
                setComments(commentsData);
            }
        } catch (err) {
            console.error('Error fetching comments:', err);
        }
    };

    const handleCommentSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!post || !newComment.trim() || !user) {
            setCommentStatus({
                success: false,
                message: 'Please enter a comment and make sure you are logged in'
            });
            return;
        }
        
        try {
            const response = await fetch(`${API_URL}/blog/${post.id}/comments`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('sessionId')}`,
                },
                body: JSON.stringify({ 
                    content: newComment,
                    parentId: replyingTo
                }),
            });
            
            const result = await response.json();
            
            if (response.ok) {
                setCommentStatus({
                    success: true,
                    message: 'Comment added successfully'
                });
                
                // Refresh comments to get the updated structure with replies
                await fetchComments(post.id);
                
                // Clear the comment form and reset reply state
                setNewComment('');
                setReplyingTo(null);
            } else {
                setCommentStatus({
                    success: false,
                    message: result.message || 'Failed to add comment'
                });
            }
        } catch (err) {
            console.error('Error adding comment:', err);
            setCommentStatus({
                success: false,
                message: 'An error occurred while adding the comment'
            });
        }
    };

    const handleBeginEdit = (comment: BlogComment) => {
        setEditingCommentId(comment.id);
        setEditContent(comment.content);
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditContent('');
    };

    const handleSubmitEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!post || !editingCommentId || !editContent.trim()) return;
        try {
            const response = await fetch(`${API_URL}/blog/${post.id}/comments/${editingCommentId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('sessionId')}`,
                },
                body: JSON.stringify({ content: editContent }),
            });
            const result = await response.json();
            if (response.ok) {
                setCommentStatus({ success: true, message: 'Comment updated successfully' });
                await fetchComments(post.id);
                handleCancelEdit();
            } else {
                setCommentStatus({ success: false, message: result.message || 'Failed to update comment' });
            }
        } catch (err) {
            console.error('Error updating comment:', err);
            setCommentStatus({ success: false, message: 'An error occurred while updating the comment' });
        }
    };

    const handleDeleteComment = async (commentId: string) => {
        if (!post || !confirm('Are you sure you want to delete this comment?')) {
            return;
        }
        
        try {
            const response = await fetch(`${API_URL}/blog/${post.id}/comments/${commentId}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('sessionId')}`,
                },
            });
            
            const result = await response.json();
            
            if (response.ok) {
                setCommentStatus({
                    success: true,
                    message: 'Comment deleted successfully'
                });
                
                // Refresh comments to get the updated structure
                await fetchComments(post.id);
            } else {
                setCommentStatus({
                    success: false,
                    message: result.message || 'Failed to delete comment'
                });
            }
        } catch (err) {
            console.error('Error deleting comment:', err);
            setCommentStatus({
                success: false,
                message: 'An error occurred while deleting the comment'
            });
        }
    };

    const renderPostContent = (content: string) => {
        try {
            // Check if content is in Lexical format
            const isLexical = content.includes('"root"') && 
                             content.includes('"children"') && 
                             content.includes('"type"');
            
            if (isLexical) {
                const htmlContent = lexicalToHtml(content);
                return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
            }
            
            // Check if content is DraftJS format
            if (content.startsWith('{') && content.includes('blocks')) {
                try {
                    const rawContent = JSON.parse(content);
                    if (isValidDraftJs(rawContent)) {
                        const contentState = convertFromRaw(rawContent);
                        const htmlContent = stateToHTML(contentState);
                        return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
                    }
                } catch (err) {
                    console.error('Error parsing DraftJS content:', err);
                }
            }
        } catch {
            // Fallback: treat as HTML if parsing fails
        }
        
        // Simple HTML content
        return <div dangerouslySetInnerHTML={{ __html: content }} />;
    };

    if (loading) {
        return <div className="loading-container">Loading...</div>;
    }

    if (error || !post) {
        return (
            <div className="error-container">
                <h2>Error</h2>
                <p>{error || 'Blog post not found'}</p>
                <Link to="/blog" className="back-link">← Back to Blog</Link>
            </div>
        );
    }

    return (
        <div className="blog-post-container">
                <div className="blog-post-header">
                    <Link to="/blog" className="back-link">← Back to Blog</Link>
                    <h1>{post.title}</h1>
                    <div className="post-meta">
                        <span>By {post.author}</span>
                        <span>on {new Date(post.createdAt).toLocaleDateString()}</span>
                        {post.updatedAt !== post.createdAt && 
                            <span>(Updated: {new Date(post.updatedAt).toLocaleDateString()})</span>}
                    </div>
                </div>

                {commentStatus && (
                    <div className={`comment-status ${commentStatus.success ? 'success' : 'error'}`}>
                        {commentStatus.message}
                    </div>
                )}

                <div className="post-content">
                    {renderPostContent(post.content)}
                </div>
                
                {/* Comments Section */}
                {post.commentsEnabled && (
                    <div className="comments-section">
                        <h3>Comments</h3>
                        
                        {/* Comment Form */}
                        {user ? (
                            <form onSubmit={handleCommentSubmit} className="comment-form">
                                <textarea
                                    value={newComment}
                                    onChange={(e) => setNewComment(e.target.value)}
                                    placeholder="Write a comment..."
                                    rows={3}
                                    required
                                />
                                <button type="submit" disabled={!newComment.trim()}>
                                    Post Comment
                                </button>
                            </form>
                        ) : (
                            <p className="login-prompt">
                                <Link to="/login">Log in</Link> to leave a comment.
                            </p>
                        )}
                        
                        {/* Comments List */}
                        <div className="comments-list">
                            {comments
                                .filter(comment => !comment.parentId) // Only top-level comments
                                .map(comment => (
                                    <div key={comment.id} className="comment">
                                        <div className="comment-header">
                                            <span className="comment-author">{comment.author}</span>
                                            <span className="comment-date">
                                                {new Date(comment.createdAt).toLocaleDateString()} {comment.edited && <em>(edited)</em>}
                                            </span>
                                            {isAdmin && (
                                                <div className="comment-actions">
                                                    <button 
                                                        onClick={() => handleBeginEdit(comment)}
                                                        className="edit-comment-btn"
                                                        title="Edit comment"
                                                    >
                                                        ✎
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteComment(comment.id)}
                                                        className="delete-comment-btn"
                                                        title="Delete comment"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {editingCommentId === comment.id ? (
                                            <form onSubmit={handleSubmitEdit} className="reply-form">
                                                <textarea
                                                    value={editContent}
                                                    onChange={(e) => setEditContent(e.target.value)}
                                                    rows={3}
                                                    required
                                                />
                                                <div className="reply-form-buttons">
                                                    <button type="submit" disabled={!editContent.trim()}>Save</button>
                                                    <button type="button" onClick={handleCancelEdit}>Cancel</button>
                                                </div>
                                            </form>
                                        ) : (
                                            <div className="comment-content">{comment.content}</div>
                                        )}
                                        
                                        {/* Reply button */}
                                        {user && (
                                            <button 
                                                onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                                                className="reply-btn"
                                            >
                                                {replyingTo === comment.id ? 'Cancel Reply' : 'Reply'}
                                            </button>
                                        )}
                                        
                                        {/* Reply form */}
                                        {replyingTo === comment.id && user && (
                                            <form onSubmit={handleCommentSubmit} className="reply-form">
                                                <textarea
                                                    value={newComment}
                                                    onChange={(e) => setNewComment(e.target.value)}
                                                    placeholder="Write a reply..."
                                                    rows={2}
                                                    required
                                                />
                                                <div className="reply-form-buttons">
                                                    <button type="submit" disabled={!newComment.trim()}>
                                                        Post Reply
                                                    </button>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => setReplyingTo(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </form>
                                        )}
                                        
                                        {/* Replies */}
                                        {comment.replies && comment.replies.length > 0 && (
                                            <div className="replies">
                                                {comment.replies.map(reply => (
                                                    <div key={reply.id} className="reply">
                                                        <div className="comment-header">
                                                            <span className="comment-author">{reply.author}</span>
                                                            <span className="comment-date">
                                                                {new Date(reply.createdAt).toLocaleDateString()} {reply.edited && <em>(edited)</em>}
                                                            </span>
                                                            {isAdmin && (
                                                                <div className="comment-actions">
                                                                    <button 
                                                                        onClick={() => handleBeginEdit(reply)}
                                                                        className="edit-comment-btn"
                                                                        title="Edit comment"
                                                                    >
                                                                        ✎
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => handleDeleteComment(reply.id)}
                                                                        className="delete-comment-btn"
                                                                        title="Delete comment"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {editingCommentId === reply.id ? (
                                                            <form onSubmit={handleSubmitEdit} className="reply-form">
                                                                <textarea
                                                                    value={editContent}
                                                                    onChange={(e) => setEditContent(e.target.value)}
                                                                    rows={2}
                                                                    required
                                                                />
                                                                <div className="reply-form-buttons">
                                                                    <button type="submit" disabled={!editContent.trim()}>Save</button>
                                                                    <button type="button" onClick={handleCancelEdit}>Cancel</button>
                                                                </div>
                                                            </form>
                                                        ) : (
                                                            <div className="comment-content">{reply.content}</div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </div>
    );
};

// Import DraftJS functions
import { convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { lexicalToHtml } from '../utils/lexicalToHtml';

// Helper function to check if content is valid DraftJS format
const isValidDraftJs = (content: any): boolean => {
    return content && 
           typeof content === 'object' && 
           content.blocks && 
           Array.isArray(content.blocks) &&
           content.entityMap !== undefined;
};

export default BlogPostView; 